import {classNames, React, observer, useLocalStore, _, useEffect, useMemo, Suspense, useAsObservableSource, lodash_removePrivateFieldsCloneCustomizer, Popover2} from '@api'
import {BuilderCard, BuilderCardProps, CatalogItemDetailsStore, cloneDeepNoPrivateFields, GroupByGroupStore, useGroupByGroupStore, useVipLocalStore, VipLocalStore} from '@modules'
import {usePresentationModeContentStore} from '@modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/PresentationMode'
import {autorun, computed, observable, runInAction, toJS, trace} from 'mobx'
import {useContext} from 'react'
import * as css from './BuilderCardWithPopover.css'
import {OFFLINE_ID, onSiteError, StoresInstance, ui, useCatalog} from '@stores'

class BuilderCardWithPopoverStore extends VipLocalStore {
  constructor(stores: StoresInstance,public props: { productCardProps?: Partial<BuilderCardProps>, extraCardProps: Partial<BuilderCardProps>, item: any, groupByStore: GroupByGroupStore }) {
    super(stores)
  }

  @computed get nested() {
    const {nested} = (this.props.extraCardProps || {})
    return nested
  }

  @computed get idWithPath() {
    const {props: {groupByStore, item}, nested, catalog: {idFor}} = this
    return `${groupByStore ? `${groupByStore.pathLookup}.` : ''}${nested?.parentData ? `${idFor(nested?.parentData)}.` : ''}${nested?.data ? idFor(nested?.data) : idFor(item)}`
  }

  @computed get localItemData() { return this.catalog.settings.perItem[this.idWithPath]}

  @computed get localItem() { return this.catalog.settings.perItem[this.idWithPath] }

  @computed get levelOfDetail() {
    const {nested, props: {extraCardProps, groupByStore}, localItemData, catalog: {settings, groupBy}} = this
    return (nested ? extraCardProps?.levelOfDetail : null) ?? (localItemData?.lod ?? groupByStore?.levelOfDetail ?? settings.levelOfDetail)
  }

  @observable.ref _itemDetailsStore: CatalogItemDetailsStore
  @observable _needsDetailsStore = false
  get itemDetailsStore(): CatalogItemDetailsStore {
    const {_itemDetailsStore, _needsDetailsStore} = this
    _needsDetailsStore && setTimeout(() => this._needsDetailsStore = true, 1)

    return _itemDetailsStore
  }

  @computed get productCardProps() {
    const {props: {productCardProps}, itemDetailsStore} = this
    return {...productCardProps, itemDetailsStore, data: this.dataWithFiltersApplied}
  }

  @computed get noPopover() {
    const {localItemData, levelOfDetail} = this
    return localItemData?.lod === 1 || levelOfDetail == 1 //|| (sidebar.right.open && sidebar.right.type == 'modal')
  }

  @computed get isOfflineItem() {
    return this.catalog.isOfflineItem(this.props.item)
  }

  @computed get builderCardIndex() {
    return this.props.productCardProps.workspace?.catalog?.index ?? this.catalog.index
  }

  @computed get builderCardStore() {
    return this.catalog.componentStores.cards[this.idWithPath]
  }

  registerEffects = () => {
    const {isOfflineItem, catalog} = this
    let shouldReplaceOfflineDetailsData = false
    const toDispose = [
      isOfflineItem && autorun(() => {
        const {itemDetailsStore} = this
        if (itemDetailsStore) {
          const {dataWithFiltersApplied: data} = this,
            clone = cloneDeepNoPrivateFields(data)
          if (!shouldReplaceOfflineDetailsData) {
            shouldReplaceOfflineDetailsData = true
          } else {
            // console.log(`Replace data on ${this.idWithPath}`)

            // Below calls will clone the above data, touching every property in it
            // If Pending Items stop working for offline changes (see YFTS-8712) check here
            itemDetailsStore.replaceData(clone)
          }
        }
      }, {name: `Clone changes for Pending Item '${this.idWithPath}' into associated detail store when mutated externally - YFTS-8712`}),
      autorun(() => {
        if (this._needsDetailsStore || this.levelOfDetail === 1) {
          const {idWithPath, dataWithFiltersApplied, catalog, builderCardIndex} = this
          this._itemDetailsStore = catalog.itemDetailStoreFor(idWithPath, dataWithFiltersApplied, builderCardIndex)
        }
      }),
      autorun(() => {
        if (!this.builderCardStore?.levelOfDetail) return null

        const {flexLayout: {refetchAllCatalogRelatedTabs}, catalog: {sidebar, settings: {autoSaveInlineChanges}, filter}, itemDetailsStore} = this

        if (!itemDetailsStore) return
        if (autoSaveInlineChanges && itemDetailsStore.isFrozen) return

        const {itemDetailsStore: {isDirty, dirtyPaths, isFrozen, isApplyingMutations}, localItem, idWithPath} = this

        if (isApplyingMutations) return

        // DEBUG && trace()
        runInAction(async () => {
          if (isDirty && !itemDetailsStore.mutationError) {
            if (autoSaveInlineChanges) {
              if (!isFrozen) {
                const refetchCatalog: boolean = dirtyPaths.has('secondaries') || dirtyPaths.has('styleCandidates') || filter.filtersWithSelections.some(({filterField}) => dirtyPaths.has(filterField))
                await itemDetailsStore.applyMutations(refetchCatalog)

                refetchAllCatalogRelatedTabs()
                if (sidebar.right.open && sidebar.right?.details?.itemDetailsStore?.id === itemDetailsStore.id) {
                  sidebar.right.details.loadData()
                }
              }
            } else {
              //itemDetailsStore.saveUnsavedChangesToLocalStorage()
              const {outstandingChanges} = itemDetailsStore
              if (!localItem) {
                catalog.settings.perItem[idWithPath] = {unsavedChanges: outstandingChanges}
              } else {
                // Save the delta into local storage
                localItem.unsavedChanges = outstandingChanges
              }
            }
          } else if (localItem) {
            localItem.unsavedChanges = undefined
          }
        })
      }, {name: `Save inline changes for ${this.idWithPath}`})
    ]

    return () => toDispose.filter(_.identity).forEach(d => d())
  }

  @computed get dataWithFiltersApplied() {
    // Filter any nested fields
    const {catalog: {pathToField, _fieldByName, filter: {filtersWithSelections}}, catalog, props: {item}} = this

    let r = item

    try {
      if (!_.isEmpty(catalog.features.toolbarSwitches)) {
        catalog.features.toolbarSwitches.forEach(({field}) => {
          const {settings: {toolbarSwitches}} = catalog

          if (toolbarSwitches[field] !== false) {
            const f = catalog._fieldByName[field],
              path = pathToField(f),
              refListIndex = path.findIndex(f => f.def?.type === 'RefList')

            if (refListIndex != -1) {
              const arrField = path[refListIndex].field,
                subField = _.last(path).field.substring(arrField.length + 1)

              let arr = _.get(item, arrField) as Array<any> ?? []
              // console.log({ arr, subField, arrField})
              if (_.isArray(arr)) {
                let filtered = arr?.filter(v => {
                  const currentValue = _.get(v, subField)

                  return currentValue === true
                }) ?? []
                if (filtered.length != arr?.length) {
                  if (r == item) {
                    r = _.cloneDeep(item)
                  }
                  _.set(r, arrField, filtered)
                }
              }
            }
          }
        })
      }

      /***
       * {@see CatalogStore.filterNested}
       * Filter invalid data immediately because we don't have nested data types for many things in ES.  Example style.colorways.attribute.Active = true
       */
      filtersWithSelections.forEach(({filterField, state: {selected, excluded}}) => {
        const field = _fieldByName[filterField]
        const path = pathToField(field),
          refListIndex = path.findIndex(f => f.def?.type === 'RefList')

        if (refListIndex != -1) {
          const arrField = path[refListIndex].field,
            subField = _.last(path).field.substring(arrField.length + 1)

          let arr = (_.get(r, arrField) as Array<any>) ?? []
          // console.log({d, arr, subField, arrField})

          if (_.isObject(arr) && !_.isArray(arr)) {
            DEBUG && console.info(`Invalid data found on record ${catalog.index} - '${catalog.idFor(r)}' - the field ${arrField} is a RefList but the current data in ES is a scalar!  This needs to be fixed by the importer at startup when the configuration is changed.`, toJS({shouldBeAnArray: arr, item, subField}, {recurseEverything: true}) )
            arr = [arr]
          }

          let filtered = !_.isArray(arr) ? [] : arr.filter(v => {
            const currentValue = _.get(v, subField)

            if (field.agg === 'exists') {
              if (selected.size) {
                if ((selected.has(true) && !_.isEmpty(currentValue)) || (selected.has(false) && _.isEmpty(currentValue))) {
                  return true
                }
              }
              if (excluded.size) {
                if ((excluded.has(true) && !_.isEmpty(currentValue)) || (excluded.has(false) && _.isEmpty(currentValue))) {
                  return false
                }
              }
            } else if (!_.isUndefined(currentValue) && !_.isNull(currentValue)) {
              if ((selected.size && !selected.has(currentValue) && !selected.has(currentValue.toString())) || (excluded.size && (excluded.has(currentValue) || excluded.has(currentValue.toString())))) {
                return false
              }
            }
            //console.log({currentValue, v, d, arr, subField, arrField, selected, excluded})
            return true
          })

          if (filtered.length != _.size(arr)) {
            if (r == item) {
              r = _.cloneDeep(item)
            }
            _.set(r, arrField, filtered)
          }
        }
      })
    } catch (err) {
      onSiteError(err)
    }

    return r
  }
}

export const BuilderCardWithPopoverStoreContext = React.createContext<BuilderCardWithPopoverStore>(undefined)
export const useBuilderCardWithPopoverStore = () => useContext(BuilderCardWithPopoverStoreContext)

export const BuilderCardWithPopoverContainer = observer<{ className?: string, item: any, productCardProps?: Partial<BuilderCardProps>, extraCardProps?: Partial<BuilderCardProps>, onContextMenu?: React.MouseEventHandler<any> }>(
  function BuilderCardWithPopoverContainer({className, item, extraCardProps, ...props}) {
    // trace()
    const groupByStore = useGroupByGroupStore(),
      presentation = usePresentationModeContentStore(),
      obsProps = useAsObservableSource({extraCardProps, item, groupByStore, productCardProps: props.productCardProps}),
      store = useVipLocalStore((ws) => new BuilderCardWithPopoverStore(ws,obsProps)),
      {builderCardStore, catalog, catalog: {isShowingPresentation, settings: {showOnlyInlineCatalogItems}}, localItemData, idWithPath, levelOfDetail, productCardProps, noPopover, dataWithFiltersApplied} = store
    // console.log({id, idWithPath, hoverPopoverItemId, noPopover, shouldHavePopover})

    useEffect(() => store.registerEffects(), [])


    if ((showOnlyInlineCatalogItems && catalog.anyLocalLODCatalogItems && !localItemData?.lod)) return null

    // if (extraCardProps?.nested) {
    //   console.log({noPopover, parent, idWithPath, shouldHavePopover, hoverPopoverItemId})
    // }
    /*
    wrapperProps: stackView && !isProductView ? {
                      'data-index': i,
                      'data-selected': selectedIds.has(item._id),
                      style: {
                        '--stack-index': i
                      } //calc(var(--card-stack-negative-margin) * ${i}.0))
                    } : {}
     */


    return <BuilderCardWithPopoverStoreContext.Provider value={store}>
      <BuilderCardWithPopover
        id={idWithPath}
        item={dataWithFiltersApplied}
        className={classNames(css.popoverContainer, className)}
        data-lod={levelOfDetail}
        data-matches-hover-item={builderCardStore?.matchesHoverItem}
        data-drilldown={catalog.isDrilldown(idWithPath)}
        {...props}
        productCardProps={productCardProps}
        extraCardProps={extraCardProps}
        noPopover={noPopover || (isShowingPresentation && !presentation)}
        levelOfDetail={levelOfDetail}
      />
    </BuilderCardWithPopoverStoreContext.Provider>
  })

BuilderCardWithPopoverContainer.displayName = 'BuilderCardWithPopoverContainer'
BuilderCardWithPopoverContainer['whyDidYouRender'] = true

export const BuilderCardWithPopover = observer<{ id, className?: string, item, noPopover?: boolean, levelOfDetail?: number, productCardProps?: Partial<BuilderCardProps>, extraCardProps?: Partial<BuilderCardProps> }>(
  function BuilderCardWithPopover({id, item, className, noPopover, levelOfDetail, productCardProps, extraCardProps, ...props}) {
    //const [ref, {}] = useMeasure() // Todo - track x/y so we can determine row/column for cards and implement up/down arrow keys
    const catalog = useCatalog(),
      isOpen = id === catalog.hoverPopoverItemId,
      disabled = noPopover || !catalog.hoverPopoverItemId

    // useEffect(() => {
    //   console.log(`Mount BuilderCardWithPopover`)
    //   return () => console.log(`Unmount BuilderCardWithPopover`)
    // }, [])
    return (
      <div
        data-id={id}
        data-inline-details={catalog.settings.perItem[id]?.lod === 1}
        data-offline={(item?._id ?? item?.id ?? item?.exId)?.startsWith(OFFLINE_ID)}
        data-type="BuilderCardWrapper"
        className={classNames(css.productWrapper, className)}
        ref={catalog.setBuilderCardRef(id)}
        {...props}
      >
        <Popover2
          // Better performance, worse GUI and no zoom behavior
          // Interferes with inline editing
          lazy
          // usePortal={true}
          content={<Suspense fallback={null}>
            <BuilderCard {...(productCardProps ?? {})} {...(extraCardProps ?? {})}
                                data={item} inlinePopover isPopover/>
          </Suspense>}
          interactionKind={'hover'}
          disabled={disabled}
          isOpen={isOpen}
          onInteraction={disabled || ui.settings.cardPopover.interactionKind !== 'hover' ? null : (nextOpenState) => {
            /*if (nextOpenState) {
              catalog.hoverPopoverItemId = id
            } else*/ if (!nextOpenState && catalog.hoverPopoverItemId === id && ui.settings.cardPopover.interactionKind === 'hover') {
              console.log(`Closing popover because blueprint told us to`)
              catalog.hoverPopoverItemId = null
            }
          }}
          position={extraCardProps?.nested?.isInCardField ? 'right' : 'bottom-left'}
          autoFocus={false}
          // hoverOpenDelay={ms}
          hoverCloseDelay={ui.settings.cardPopover.closeMs}
          popoverClassName={css.productPopover}
          // minimal={false}
          // modifiers={WINDOW_OVERFLOW_POPOVER_MODIFIERS}
        >
          <BuilderCard
            // levelOfDetail={levelOfDetail} // Let the card determine this itself to avoid everyone rendering simultaneously
            {...(productCardProps ?? {})} {...(extraCardProps ?? {})} data={item}/>
        </Popover2>
      </div>
    )
  })
BuilderCardWithPopover.displayName = 'BuilderCardWithPopover'



// WEBPACK FOOTER //
// ./src/modules/catalog/product/productCard/components/BuilderCard/BuilderCardWithPopover.tsx